
import { Id, Review, ReSession, ReEvent } from '../../interfaces/types';
import { Alert, Box, Button, FormControl, Slider, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import uuidv4 from 'uuid-random';
import { useUserContext } from '../../providers/userProvider';
import InfoTooltip from '../common/InfoTooltip/InfoTooltip';
import { useReviewContext } from '../../providers/ReviewProvider';
import { useEventContext } from '../../providers/EventProvider';

interface ReviewFormProps {
    session: ReSession;
    editForm?: boolean;
    review?: Review;
    onClose?: () => void;
}

const ReviewForm: FC<ReviewFormProps> = ({ session, editForm = false, review, onClose }) => {
    const { userDetails } = useUserContext();
    const { addReview, updateReview } = useReviewContext();
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
    const { isXpFactorActive, getEventBySessionId } = useEventContext();

    const defaultReview: Review = {
        id: editForm ? review?.id as Id : uuidv4(),
        sessionId: editForm ? review?.sessionId as Id : session?.id,
        createdBy: editForm ? review?.createdBy as Id : userDetails?.uid as Id,
        createdAt: editForm ? review?.createdAt as Date : new Date(),
        isNew: true,
        whatIlike: editForm ? review?.whatIlike as string : '',
        improvementOpportunities: editForm ? review?.improvementOpportunities as string : '',
        xpFactor: editForm ? review?.xpFactor as number : 0,
        score: editForm ? review?.score as number : 0,
    };
    const [newReview, setNewReview] = useState<Review>(defaultReview);
    const [currentEvent, setCurrentEvent] = useState<ReEvent>();

    useEffect(() => {
        const event = getEventBySessionId(newReview.sessionId);
        if(event) setCurrentEvent(event);
    }, [newReview.sessionId]);

    const handleCloseSnackbar = (
        event?: React.SyntheticEvent | Event,
        reason?: string
    ) => {
        if (reason === "clickaway") {
            return;
        }
        setOpenSnackbar(false);
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        try {
            if (newReview.whatIlike.trim() === '' || newReview.improvementOpportunities.trim() === '') throw new Error('Please fill in all fields before submitting a review!');
            if (editForm) {
                updateReview(newReview);
                onClose?.();
            }
            else {
                addReview(newReview);
                setNewReview(defaultReview);
            }

            setSnackbarMessage("Review successfully submitted!");
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
        } catch (error) {
            setSnackbarMessage("Review submit has failed!");
            if (error instanceof Error && error.message === 'Please fill in all fields before submitting a review!') setSnackbarMessage(error.message);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };

    return (
        <Box component={'form'} onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Stack spacing={2} sx={{ width: '100%' }}>
                <FormControl fullWidth>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="What I like:"
                        value={newReview.whatIlike}
                        onChange={(e) => setNewReview({ ...newReview, whatIlike: e.target.value })}
                    />
                </FormControl>
                <FormControl fullWidth>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="What can be improved:"
                        value={newReview.improvementOpportunities}
                        onChange={(e) => setNewReview({ ...newReview, improvementOpportunities: e.target.value })}
                    />
                </FormControl>
                {currentEvent && isXpFactorActive(currentEvent) && <FormControl fullWidth>
                    <Typography id="xp-factor-slider-label" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        XP factor: {newReview?.xpFactor}
                        <InfoTooltip message="The XP factor indicates the extremeness of this session. How thought-provoking, exiting, eXtreme,... is this session?" />
                    </Typography>
                    <Slider
                        aria-labelledby="xp-factor-slider-label"
                        valueLabelDisplay="off" //do not display value on the slider itself
                        value={newReview?.xpFactor}
                        min={0}
                        max={10}
                        step={1}
                        marks
                        onChange={(e, value) => {
                            setNewReview({
                                ...newReview,
                                xpFactor: value as number,
                            });
                        }}
                    />
                </FormControl>}
                <FormControl fullWidth>
                    <Typography id="score-slider-label" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        How would you score this session:
                    </Typography>
                    <Slider
                        aria-labelledby="score-slider-label"
                        valueLabelDisplay="auto" //do not display value on the slider itself
                        value={newReview?.score}
                        min={0}
                        max={10}
                        step={1}
                        marks
                        onChange={(e, value) => {
                            setNewReview({
                                ...newReview,
                                score: value as number,
                            });
                        }}

                    />
                </FormControl>
                <FormControl fullWidth>
                    <Button type='submit' variant='contained' color='primary'>{editForm ? "Save Review" : "Submit Review"}</Button>
                </FormControl>

                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbarSeverity}
                        sx={{ width: "100%" }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Stack>
        </Box>
    );
};

export default ReviewForm;